// PAB Collection grid — faceted filtering via the Section Rendering API.
// All listeners are delegated on the custom element root so they survive
// the innerHTML swaps that happen on every filter/sort/page change.
class PabCollection extends HTMLElement {
  #seq = 0;
  #popstate = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#popstate = () => this.#apply(location.href, { push: false });
    window.addEventListener('popstate', this.#popstate);

    // Design behavior: arriving with filters in the URL scrolls to the grid.
    if (/[?&]filter\./.test(location.search)) {
      setTimeout(() => this.#scrollToGrid(), 120);
    }
  }

  disconnectedCallback() {
    if (this.#popstate) window.removeEventListener('popstate', this.#popstate);
  }

  #onClick = (e) => {
    const toggle = e.target.closest('[data-pab-fsec-toggle]');
    if (toggle) {
      const closed = toggle.closest('[data-pab-fsec]').classList.toggle('closed');
      toggle.setAttribute('aria-expanded', String(!closed));
      return;
    }
    if (e.target.closest('[data-pab-drawer-open]')) {
      this.setAttribute('data-drawer-open', '');
      return;
    }
    if (e.target.closest('[data-pab-drawer-close]') || e.target.closest('[data-pab-scrim]')) {
      this.removeAttribute('data-drawer-open');
      return;
    }
    const link = e.target.closest('a[data-pab-ajax]');
    if (link) {
      e.preventDefault();
      this.#apply(link.href, { scroll: link.hasAttribute('data-pab-page') });
    }
  };

  #onChange = (e) => {
    if (e.target.matches('[data-pab-filter]')) {
      e.target.closest('.sa-check')?.classList.toggle('on', e.target.checked);
      this.#apply(this.#buildUrl());
    } else if (e.target.matches('[data-pab-sort]')) {
      this.#apply(this.#buildUrl());
    }
  };

  #buildUrl() {
    const params = new URLSearchParams();
    this.querySelectorAll('[data-pab-filter]:checked').forEach((input) => params.append(input.name, input.value));
    const sort = this.querySelector('[data-pab-sort]')?.value;
    if (sort) params.set('sort_by', sort);
    const qs = params.toString();
    return location.pathname + (qs ? `?${qs}` : '');
  }

  async #apply(url, { push = true, scroll = false } = {}) {
    const seq = ++this.#seq;
    this.classList.add('loading');
    try {
      const fetchUrl = new URL(url, location.origin);
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
      const res = await fetch(fetchUrl);
      if (!res.ok) throw new Error(`Section render failed: ${res.status}`);
      const text = await res.text();
      if (seq !== this.#seq) return; // superseded by a newer request

      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fresh = doc.querySelector('pab-collection');
      if (!fresh) throw new Error('Section markup missing from response');
      this.innerHTML = fresh.innerHTML;

      if (push) history.pushState({ pab: 1 }, '', new URL(url, location.origin));
      if (scroll) this.#scrollToGrid();
    } catch (err) {
      console.error('[pab-collection]', err);
      location.href = url; // graceful fallback: normal navigation
    } finally {
      if (seq === this.#seq) this.classList.remove('loading');
    }
  }

  #scrollToGrid() {
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 68;
    window.scrollTo({
      top: this.getBoundingClientRect().top + window.scrollY - header - 12,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('pab-collection')) customElements.define('pab-collection', PabCollection);
